import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  Zap,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const GMP_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-blue-400" />
            <span>Manufacturing Quality Standards</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400">
            GMP Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Good Manufacturing Practices - Ensuring Safe, Effective, and Quality
            Products Through Controlled Manufacturing
          </p>
        </motion.div>
      </section>

      {/* What is GMP */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is GMP Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              Good Manufacturing Practices (GMP) are a set of guidelines and
              principles that ensure that products are consistently produced and
              controlled according to quality standards. GMP is critical for
              organizations in regulated industries such as food,
              pharmaceuticals, cosmetics, medical devices, and consumer goods to
              ensure the production of safe and effective products.
            </p>
            <p>
              GMP certification confirms that an organization has implemented
              and follows strict Good Manufacturing Practices to ensure
              consistent product quality, safety, and compliance with relevant
              regulations. The certification focuses on quality control,
              equipment maintenance, employee training, clean manufacturing
              environments, and comprehensive record-keeping.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of GMP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Product Safety and Quality",
                desc: "Ensures that products are consistently produced to meet safety, efficacy, and quality standards.",
                icon: ShieldCheck,
              },
              {
                title: "Regulatory Compliance",
                desc: "Ensures compliance with FDA, EMA, WHO, and other regulatory bodies worldwide.",
                icon: CheckCircle,
              },
              {
                title: "Risk-Based Approach",
                desc: "Identifies potential risks in the manufacturing process and implements effective controls.",
                icon: Target,
              },
              {
                title: "Traceability and Documentation",
                desc: "Requires proper documentation and record-keeping for complete traceability.",
                icon: FileCheck,
              },
              {
                title: "Continuous Improvement",
                desc: "Encourages regular audits, inspections, and updates to manufacturing practices.",
                icon: TrendingUp,
              },
              {
                title: "Equipment & Facility Control",
                desc: "Ensures equipment is maintained, cleaned, and calibrated for consistent quality.",
                icon: Building2,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-blue-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Good Manufacturing Practice */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is Good Manufacturing Practice (GMP)?
          </h2>
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              Good Manufacturing Practices (GMP) are a set of quality management
              principles and practices that cover all aspects of the
              manufacturing process, including:
            </p>
            <ul className="space-y-3">
              {[
                "Raw Material Control - Ensuring raw materials are of the highest quality",
                "Process Control - Ensuring manufacturing processes are validated and repeatable",
                "Equipment and Facility Maintenance - Ensuring equipment is maintained and calibrated",
                "Employee Training - Ensuring employees are trained in GMP principles and safety",
                "Sanitation - Maintaining a clean and safe manufacturing environment",
                "Packaging and Labeling - Ensuring products are properly packaged and labeled",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of GMP Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Ensures products are consistently safe and of high quality",
              "Meets regulatory requirements from FDA, EMA, and local authorities",
              "Demonstrates commitment to quality and customer safety",
              "Reduces recalls, lawsuits, and regulatory fines",
              "Streamlines production and improves operational efficiency",
              "Provides competitive advantage and market credibility",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-cyan-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of GMP Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            GMP certification helps organizations:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Demonstrate commitment to quality, safety, and customer satisfaction",
              "Improve operational efficiency and reduce production risks",
              "Meet legal and regulatory requirements for product safety",
              "Gain credibility and trust in the marketplace",
              "Enhance overall organizational reputation",
              "Expand market access, especially internationally",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-blue-100 to-cyan-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-blue-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of GMP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Consistent Product Quality ensuring standards and specifications",
              "Regulatory Compliance with government and industry bodies",
              "Operational Efficiency reducing waste, rework, and quality issues",
              "Enhanced Customer Trust demonstrating commitment to quality",
              "Market Access in domestic and international markets",
              "Reduced Liability and decreased risk of recalls",
              "Improved Employee Awareness and training culture",
              "Competency Development and process standardization",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require GMP Certification
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            GMP certification is crucial for industries where product safety and
            regulatory compliance are critical:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Pharmaceuticals and Biotech",
              "Food and Beverages",
              "Cosmetics and Personal Care",
              "Medical Devices",
              "Nutritional Supplements",
              "Herbal and Natural Products",
              "Chemical Manufacturing",
              "Dietary Supplements",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get GMP Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Gap Analysis",
                desc: "Conduct a thorough review of current manufacturing practices against GMP requirements",
              },
              {
                step: 2,
                title: "GMP Plan Development",
                desc: "Develop a detailed GMP plan with quality control and management procedures",
              },
              {
                step: 3,
                title: "Facility Setup",
                desc: "Ensure facilities meet necessary cleanliness, safety, and operational requirements",
              },
              {
                step: 4,
                title: "Employee Training",
                desc: "Train all employees on GMP principles, safety protocols, and quality practices",
              },
              {
                step: 5,
                title: "Internal Audits",
                desc: "Regularly audit processes to identify areas for improvement",
              },
              {
                step: 6,
                title: "Certification Audit",
                desc: "Undergo formal audit by an accredited certification body",
              },
              {
                step: 7,
                title: "Continuous Improvement",
                desc: "Continuously monitor and improve GMP processes",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-50 via-cyan-50 to-teal-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for GMP Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides expert GMP consultancy services
              to help organizations implement, maintain, and continuously
              improve their Good Manufacturing Practices across all regulated
              industries.
            </p>
            <p className="text-lg text-gray-700 mb-6">Our services include:</p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "GMP Plan Development and customization",
                "Facility and Equipment Setup consultation",
                "Comprehensive Employee Training programs",
                "Internal Audits and compliance verification",
                "Pre-certification audit preparation",
                "Certification body liaising and support",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help organizations achieve safe, compliant, and high-quality
              production through effective GMP implementation.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default GMP_Details;
